import { FC, RefObject, useEffect, useMemo, useState } from 'react';

import { Typography } from 'antd';
import { useNavigate } from 'react-router-dom';

import { TCustomer } from 'types/customer';

import { AfterCodeActions } from '@api/enums/afterCodeActions';
import { CustomerProfileResp } from '@api/responseModels/customer/customerProfileResponse';
import { InvitationResp } from '@api/responseModels/invitation/invitationResponse';
import { ConnectionResp } from '@api/responseModels/security/connectionResponse';
import { CRMAPIManager } from '@classes/crmApiManager';
import { messageService } from '@classes/messageService';
import { SettingsManager } from '@classes/settingsManager';
import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useSearch } from '@hooks/useSearch';
import { buildFallbackCustomerName } from '@utils/buildFallbackCustomerName';
import { prepareRussianPhoneInput } from '@utils/russianPhoneInput';
import { TUser } from 'types/user/user';

import { CustomerCard } from '../blocks/customerCard';

import { DraggableModal } from './draggableModal';

import './incomingCallModal.scss';

type CallState = 'idle' | 'offhook' | 'ringing';

/** Данные звонка от телефонии */
export type TIncomingCallPayload = { state?: CallState; phone?: string; crm_id?: number };

type IncomingCallModalProps = {
    open: boolean;
    onClose: () => void;
    customerProfile: TIncomingCallPayload | null;
    onCreateOrderClick: (customer?: TCustomer) => void;
    dropdownRef: RefObject<HTMLDivElement>;
    openActionsDropdown: boolean;
    onToggleActionsDropdown: () => void;
    onActionClick: (action: string) => void;
};

type CallHeaderConfig = {
    color: 'green' | 'orange' | 'red';
    title: string;
};

// Статические фильтры и сортировка для поиска клиентов
const SEARCH_FILTERS = {
    toEventDate: '',
};

const SEARCH_SORTS = {
    sort_by: '',
    sort_dir: 'asc' as const,
};

const getCallHeaderConfig = (state: CallState | undefined, phone: string | undefined): CallHeaderConfig => {
    const phoneNumber = phone || 'неизвестный номер';

    switch (state) {
        case 'offhook':
            return {
                color: 'orange',
                title: `Принятый звонок от ${phoneNumber}`,
            };
        case 'idle':
            return {
                color: 'red',
                title: `Завершенный звонок от ${phoneNumber}`,
            };
        case 'ringing':
        default:
            return {
                color: 'green',
                title: `Входящий звонок от ${phoneNumber}`,
            };
    }
};

export const IncomingCallModal: FC<IncomingCallModalProps> = ({
    open,
    onClose,
    customerProfile,
    onCreateOrderClick,
    dropdownRef, 
    openActionsDropdown,
    onToggleActionsDropdown,
    onActionClick,
}) => {
    const navigate = useNavigate();
    // The modal is mounted app-wide, including /token-verification where credentials are still null.
    const crmId = customerProfile?.crm_id ?? SettingsManager.getConnectionCredentials()?.crmID;

    /**
     * Телефонии номер приходит целиком — `+79300844200`. И в базе, и в запросах
     * он живёт десятью цифрами без кода страны: `digits_between` на бэке
     * отвергает плюс, а поиск по полному номеру не находит существующего клиента.
     */
    const phone = useMemo(
        () => (customerProfile?.phone ? prepareRussianPhoneInput(customerProfile.phone) : ''),
        [customerProfile?.phone]
    );

    const headerConfig = useMemo(
        () => getCallHeaderConfig(customerProfile?.state, customerProfile?.phone),
        [customerProfile?.state, customerProfile?.phone]
    );

    // Мемоизация query, чтобы поиск запускался только при изменении номера телефона
    const searchQuery = useMemo(() => (open ? phone : ''), [open, phone]);

    // Поиск клиента по номеру телефона
    const {
        isSearchActive,
        isLoading: isSearchLoading,
        searchResult,
    } = useSearch({
        model: 'customer-profile',
        query: searchQuery,
        filters: SEARCH_FILTERS,
        sorts: SEARCH_SORTS,
        search_attributes: ['phone', 'name', 'surname'],
        crmId,
    });

    // Получаем первого найденного клиента из результата поиска
    const foundCustomer = useMemo(() => {
        if (isSearchActive && !isSearchLoading && searchResult.items.length > 0) {
            const firstItem = searchResult.items[0];
            // Данные из поиска могут иметь структуру { id, type, ...data }
            // Преобразуем в формат TCustomer
            return firstItem as unknown as TCustomer;
        }
        return null;
    }, [isSearchActive, isSearchLoading, searchResult.items]);

    // Определяем, нужно ли показывать режим создания клиента
    const isCustomerNotFound = useMemo(() => {
        return open && !!phone && isSearchActive && !isSearchLoading && !foundCustomer;
    }, [open, phone, isSearchActive, isSearchLoading, foundCustomer]);

    // Состояние для имени нового клиента
    const [newCustomerName, setNewCustomerName] = useState('');
    const [isCreatingCustomer, setIsCreatingCustomer] = useState(false);

    // Сброс имени при закрытии модалки или изменении номера телефона
    useEffect(() => {
        if (!open) {
            setNewCustomerName('');
            setIsCreatingCustomer(false);
        }
    }, [open]);

    useEffect(() => {
        setNewCustomerName('');
        setIsCreatingCustomer(false);
    }, [phone]);

    // Функция создания клиента
    const handleCreateCustomer = async (): Promise<TCustomer | null> => {
        if (!phone) {
            messageService.sendError('Номер телефона не указан');
            return null;
        }

        try {
            setIsCreatingCustomer(true);

            // Проверяем, существует ли пользователь с таким номером
            const connectionResp = await CRMAPIManager.request<ConnectionResp>(async (api) => {
                return await api.connection(phone);
            });

            if (connectionResp.errorMessages) {
                throw new Error(connectionResp.errorMessages[0]);
            }

            const isUserExists = connectionResp.data.require_method === AfterCodeActions.login;
            let customerProfileId: number;

            if (isUserExists) {
                // Пользователь зарегистрирован - приглашаем его
                const inv = await CRMAPIManager.request<InvitationResp>(async (api) => {
                    return await api.inviteRegisterUser(crmId, phone, 'customer');
                });

                if (inv.errorMessages) {
                    throw new Error(inv.errorMessages[0]);
                }

                customerProfileId = inv.data?.data?.customer?.id;
            } else {
                // Пользователь не зарегистрирован - создаем с именем
                const userData: Partial<TUser> = {
                    // имя вводить не обязательно, но бэку оно нужно непустым
                    name: newCustomerName.trim() || buildFallbackCustomerName(phone),
                    phone: phone,
                    surname: '',
                    patronymic: '',
                };
                const inv = await CRMAPIManager.request<InvitationResp>(async (api) => {
                    return await api.inviteNotRegisterUser(
                        crmId,
                        userData as TUser,
                        'customer'
                    );
                });

                if (inv.errorMessages) {
                    throw new Error(inv.errorMessages[0]);
                }

                customerProfileId = inv.data?.data?.customer?.id;
            }

            // Получаем профиль созданного клиента
            const customerResp = await CRMAPIManager.request<CustomerProfileResp>(async (api) => {
                return await api.getCustomerProfile(customerProfileId, crmId);
            });

            if (customerResp.errorMessages) {
                throw new Error(customerResp.errorMessages[0]);
            }

            messageService.sendSuccess('Клиент создан');
            return customerResp.data?.data;
        } catch (err) {
            messageService.sendError(err.message || 'Ошибка при создании клиента');
            return null;
        } finally {
            setIsCreatingCustomer(false);
        }
    };

    // Обработчик нажатия на кнопку "+ Заказ"
    const handleCreateOrderClick = async () => {
        if (foundCustomer) {
            // Клиент найден - передаем его
            onCreateOrderClick(foundCustomer);
        } else if (isCustomerNotFound) {
            // Клиент не найден - создаем его
            const createdCustomer = await handleCreateCustomer();
            if (createdCustomer) {
                onCreateOrderClick(createdCustomer);
            }
        } else {
            // Поиск еще не завершен
            onCreateOrderClick();
        }
    };

    // Обработчик действий из дропдауна
    const handleActionClick = (action: string) => {
        if (action === 'goToProfile') {
            if (foundCustomer?.id) {
                navigate(`/lk/worker/crm/${crmId}/customers/${foundCustomer.id}`);
                onClose();
            } else {
                messageService.sendError('Клиент не найден');
            }
        } else if (action === 'openOrders') {
            if (foundCustomer?.id) {
                // Переход на страницу заказов с фильтрацией по клиенту
                // Используем orderIds если они есть, иначе передаем пустой массив
                // orderIds приходит пустым при поиске, попросить Виталия присылать массив orderIds
                const customerOrderIds = 'orderIds' in foundCustomer 
                    ? (foundCustomer as TCustomer & { orderIds?: number[] }).orderIds || []
                    : [];
                navigate(`/lk/worker/crm/${crmId}/orders`, {
                    state: {
                        customerOrderIds: customerOrderIds,
                    },
                });
                onClose();
            } else {
                messageService.sendError('Клиент не найден');
            }
        } else {
            onActionClick(action);
        }
    };

    return (
        <DraggableModal
            open={open}
            onClose={onClose}
            title={headerConfig.title}
            headerColor={headerConfig.color}
        >
            <div className="call-settings-draggable-modal-content">
                {/* Карточка клиента с кнопкой создания заказа */}
                <div className="call-settings-draggable-modal-content-worker-position-card">
                    <div style={{ width: '100%' }}>
                        {foundCustomer ? (
                            <CustomerCard
                                customer={foundCustomer}
                                searchQuery={searchQuery}
                            />
                        ) : isCustomerNotFound ? (
                            <CustomerCard
                                customer={null}
                                isCreating={true}
                                customerName={newCustomerName}
                                onCustomerNameChange={setNewCustomerName}
                            />
                        ) : (
                            customerProfile && (
                                <div style={{ padding: '10px' }}>
                                    <Typography.Text>Поиск клиента...</Typography.Text>
                                </div>
                            )
                        )}
                    </div>
                    <div>
                        <div
                            className="call-settings-draggable-modal-content-worker-position-card-button"
                            onClick={handleCreateOrderClick}
                            style={{
                                opacity: isCreatingCustomer ? 0.5 : 1,
                                cursor: isCreatingCustomer ? 'not-allowed' : 'pointer',
                            }}
                        >
                            <Typography.Text>
                                {isCreatingCustomer ? 'Создание...' : '+ Заказ'}
                            </Typography.Text>
                        </div>
                    </div>
                </div>

                <Typography.Text>Всего заказов: 10</Typography.Text>

                <div
                    className="call-settings-draggable-modal-content-customer-orders-dropdown"
                    ref={dropdownRef}
                >
                    <div
                        className="call-settings-draggable-modal-content-customer-orders-dropdown-button"
                        onClick={onToggleActionsDropdown}
                    >
                        <Typography.Text>Действие</Typography.Text>
                        <div>
                            <FontAwesomeIcon
                                icon={openActionsDropdown ? faChevronUp : faChevronDown}
                                color="#1890ff"
                            />
                        </div>
                    </div>

                    {openActionsDropdown && (
                        <div className="call-settings-actions-dropdown-menu">
                            <div
                                className="call-settings-actions-dropdown-item"
                                onClick={() => handleActionClick('sendLink')}
                            >
                                <Typography.Text>Отправить ссылку на сайт</Typography.Text>
                            </div>
                            <div
                                className="call-settings-actions-dropdown-item"
                                onClick={() => handleActionClick('goToProfile')}
                            >
                                <Typography.Text>Перейти в профиль клиента</Typography.Text>
                            </div>
                            <div
                                className="call-settings-actions-dropdown-item"
                                onClick={() => handleActionClick('openOrders')}
                            >
                                <Typography.Text>Открыть список заказов</Typography.Text>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </DraggableModal>
    );
};


